import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { adminApi } from '@/lib/adminApi';

const SOURCE_OPTIONS = [
  'Instagram', 'Facebook', 'LinkedIn', 'Threads', 'X', 'YouTube', 'Email',
  'Newsletter', 'Google', 'Podcast', 'QR Code', 'Referral', 'Partner',
  'Speaking Event', 'LabFounders HQ', 'Altura Global RE', 'FLOWTALIS',
  'Move MODE', 'Between Us Convos', 'That Girl Toia XO',
];
const MEDIUM_OPTIONS = [
  'social', 'email', 'newsletter', 'video', 'podcast', 'website', 'bio',
  'qr', 'event', 'organic', 'paid', 'referral', 'speaker',
];
const COMPANY_OPTIONS = [
  'IAmToiaWallace', 'LabFounders HQ', 'Altura Global RE', 'FLOWTALIS',
  'Move MODE', 'Between Us Convos', 'That Girl Toia XO', 'Future Companies',
];
const TEMPLATES = [
  { label: 'Instagram Bio', source: 'Instagram', medium: 'bio', content: 'bio' },
  { label: 'LinkedIn Post', source: 'LinkedIn', medium: 'social', content: 'post' },
  { label: 'Facebook', source: 'Facebook', medium: 'social', content: 'post' },
  { label: 'YouTube Description', source: 'YouTube', medium: 'video', content: 'video_description' },
  { label: 'Newsletter', source: 'Newsletter', medium: 'newsletter', content: 'button' },
  { label: 'Podcast', source: 'Podcast', medium: 'podcast', content: 'show_notes' },
  { label: 'Speaker Presentation', source: 'Speaking Event', medium: 'speaker', content: 'slide_deck' },
  { label: 'QR Flyer', source: 'QR Code', medium: 'qr', content: 'qr_flyer' },
  { label: 'Business Card', source: 'QR Code', medium: 'qr', content: 'business_card' },
  { label: 'Email Signature', source: 'Email', medium: 'email', content: 'signature' },
  { label: 'Resource Download', source: 'Referral', medium: 'referral', content: 'resource' },
];

const empty = {
  name: '', destination_url: 'https://iamtoiawallace.com/founders-list',
  source: '', source_custom: '', medium: '', medium_custom: '',
  campaign: '', content: '', term: '', company: '', notes: '', status: 'active',
};

const qrUrl = (data, format = 'png') =>
  `https://api.qrserver.com/v1/create-qr-code/?size=400x400&format=${format}&data=${encodeURIComponent(data)}`;

const buildPreview = (f) => {
  try {
    const src = (f.source === 'Custom' ? f.source_custom : f.source).trim();
    const med = (f.medium === 'Custom' ? f.medium_custom : f.medium).trim();
    if (!f.destination_url || !src || !med || !f.campaign) return '';
    const url = new URL(f.destination_url);
    url.searchParams.set('utm_source', src.toLowerCase());
    url.searchParams.set('utm_medium', med.toLowerCase());
    url.searchParams.set('utm_campaign', f.campaign.trim().toLowerCase().replace(/\s+/g, '_'));
    if (f.content.trim()) url.searchParams.set('utm_content', f.content.trim().toLowerCase().replace(/\s+/g, '_'));
    if (f.term.trim()) url.searchParams.set('utm_term', f.term.trim().toLowerCase().replace(/\s+/g, '_'));
    return url.toString();
  } catch (_) { return ''; }
};

const StatCard = ({ label, value }) => (
  <div className="bg-white/70 border border-neutral-200/60 rounded-xl px-6 py-5">
    <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-3">{label}</div>
    <div style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl leading-none text-neutral-900">
      {value ?? '—'}
    </div>
  </div>
);

export default function Campaigns() {
  const [form, setForm] = useState(empty);
  const [list, setList] = useState({ items: [], summary: {} });
  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving] = useState(false);
  const [qrTarget, setQrTarget] = useState(null); // { url, name }

  const preview = useMemo(() => buildPreview(form), [form]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (companyFilter !== 'all') params.company = companyFilter;
    if (statusFilter !== 'all') params.status = statusFilter;
    const d = await adminApi.get('/admin/campaigns', { params }).then((r) => r.data);
    setList(d);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, companyFilter, statusFilter]);

  const applyTemplate = (t) => {
    setForm((f) => ({ ...f, source: t.source, medium: t.medium, content: t.content }));
  };

  const submit = async () => {
    const src = form.source === 'Custom' ? form.source_custom : form.source;
    const med = form.medium === 'Custom' ? form.medium_custom : form.medium;
    if (!form.name.trim() || !form.destination_url.trim() || !src.trim() || !med.trim() || !form.campaign.trim()) {
      toast.error('Name, Destination, Source, Medium, and Campaign are required.');
      return;
    }
    setSaving(true);
    try {
      await adminApi.post('/admin/campaigns', {
        name: form.name.trim(),
        destination_url: form.destination_url.trim(),
        source: src, medium: med,
        campaign: form.campaign.trim(),
        content: form.content.trim() || null,
        term: form.term.trim() || null,
        company: form.company || null,
        notes: form.notes.trim() || null,
        status: form.status,
      });
      toast.success('Campaign saved.');
      setForm(empty);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (t) => {
    try { await navigator.clipboard.writeText(t); toast.success('Copied.'); }
    catch (_) { toast.error('Copy failed.'); }
  };

  const patchStatus = async (id, status) => {
    await adminApi.patch(`/admin/campaigns/${id}`, { status });
    toast.success(`Marked ${status}.`);
    await load();
  };

  const duplicate = async (id) => {
    await adminApi.post(`/admin/campaigns/${id}/duplicate`);
    toast.success('Duplicated.');
    await load();
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this campaign permanently?')) return;
    await adminApi.delete(`/admin/campaigns/${id}`);
    toast.success('Deleted.');
    await load();
  };

  return (
    <div data-testid="admin-campaigns-view">
      <header className="mb-8">
        <div className="uppercase tracking-[0.28em] text-[10px] text-neutral-500 mb-2">Analytics · Utility</div>
        <h1 style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }} className="text-3xl lg:text-4xl text-neutral-900">
          Campaign Builder
        </h1>
        <p className="mt-3 text-[13px] text-neutral-500 max-w-2xl leading-relaxed">
          Create trackable marketing links for social, email, speaking, podcasts, QR codes, and paid campaigns. Every link is saved so you can review its performance later inside Google Analytics.
        </p>
      </header>

      {/* Summary */}
      <section className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard label="Total Campaigns" value={list.summary?.total ?? 0} />
        <StatCard label="Active" value={list.summary?.active ?? 0} />
        <StatCard label="Top Source" value={list.summary?.top_source || '—'} />
        <StatCard label="Top Campaign" value={list.summary?.top_campaign || '—'} />
      </section>

      {/* Builder */}
      <section className="bg-white/70 border border-neutral-200/60 rounded-xl p-6 lg:p-8 mb-10">
        <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-5">Create Campaign</div>

        {/* Templates */}
        <div className="mb-6">
          <div className="text-[11px] text-neutral-600 mb-2">Quick templates</div>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.map((t) => (
              <button key={t.label} onClick={() => applyTemplate(t)} className="text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border border-neutral-300 hover:bg-neutral-100">
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <TextField label="Campaign Name (internal)" value={form.name} onChange={(v) => set('name', v)} placeholder="e.g. Founder's Brief Launch" />
          <TextField label="Destination URL" value={form.destination_url} onChange={(v) => set('destination_url', v)} placeholder="https://iamtoiawallace.com/founders-list" />
          <SelectField label="utm_source" value={form.source} onChange={(v) => set('source', v)} options={[...SOURCE_OPTIONS, 'Custom']} />
          {form.source === 'Custom' && (
            <TextField label="Custom Source" value={form.source_custom} onChange={(v) => set('source_custom', v)} placeholder="my_custom_source" />
          )}
          <SelectField label="utm_medium" value={form.medium} onChange={(v) => set('medium', v)} options={[...MEDIUM_OPTIONS, 'Custom']} />
          {form.medium === 'Custom' && (
            <TextField label="Custom Medium" value={form.medium_custom} onChange={(v) => set('medium_custom', v)} placeholder="my_medium" />
          )}
          <TextField label="utm_campaign (required)" value={form.campaign} onChange={(v) => set('campaign', v)} placeholder="founders_brief_launch" />
          <TextField label="utm_content (optional)" value={form.content} onChange={(v) => set('content', v)} placeholder="bio | button | carousel" />
          <TextField label="utm_term (optional)" value={form.term} onChange={(v) => set('term', v)} placeholder="paid keyword" />
          <SelectField label="Company" value={form.company} onChange={(v) => set('company', v)} options={['', ...COMPANY_OPTIONS]} />
          <div className="md:col-span-2">
            <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">Notes</label>
            <textarea rows={2} value={form.notes} onChange={(e) => set('notes', e.target.value)} className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900" placeholder="Used during AI Summit · Printed on business cards…" />
          </div>
        </div>

        {/* Generated URL preview */}
        <div className="mt-6 border-t border-neutral-200/60 pt-6">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2">Generated URL</div>
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-4 py-3 text-[12.5px] font-mono text-neutral-800 break-all min-h-[52px]" data-testid="campaign-preview-url">
            {preview || <span className="text-neutral-400 font-sans italic">Fill in Destination, Source, Medium and Campaign to generate…</span>}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button onClick={() => copyText(preview)} disabled={!preview} className="text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100 disabled:opacity-40">Copy Link</button>
            <a href={preview || '#'} target="_blank" rel="noreferrer" aria-disabled={!preview} className={`text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100 ${!preview ? 'opacity-40 pointer-events-none' : ''}`}>Open Link</a>
            <button onClick={() => setQrTarget({ url: preview, name: form.name || 'campaign' })} disabled={!preview} className="text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100 disabled:opacity-40">Create QR Code</button>
            <button onClick={submit} disabled={saving || !preview} data-testid="campaign-save" className="ml-auto text-[10px] uppercase tracking-[0.2em] px-5 py-2.5 rounded-full bg-neutral-900 text-white hover:bg-[#1a1a1a] disabled:opacity-40">
              {saving ? 'Saving…' : 'Save Campaign'}
            </button>
          </div>
        </div>
      </section>

      {/* Library */}
      <section className="bg-white/70 border border-neutral-200/60 rounded-xl overflow-hidden">
        <div className="p-5 border-b border-neutral-200/60 flex flex-col md:flex-row gap-3 md:items-center">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 md:mr-auto">Campaign Library</div>
          <input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] w-full md:w-64" />
          <select value={companyFilter} onChange={(e) => setCompanyFilter(e.target.value)} className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]">
            <option value="all">All companies</option>
            {COMPANY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px]">
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-neutral-50/70">
              <tr>
                {['Campaign', 'Destination', 'Source', 'Medium', 'Status', 'Created', ''].map((h) => (
                  <th key={h} className="text-left px-4 py-3 uppercase tracking-[0.18em] text-[10px] text-neutral-500 font-normal">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200/60">
              {list.items.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-neutral-500">No campaigns yet. Build the first one above.</td></tr>
              )}
              {list.items.map((c) => (
                <tr key={c.id}>
                  <td className="px-4 py-3">
                    <div className="text-neutral-900">{c.name}</div>
                    <div className="text-[11px] text-neutral-500">{c.campaign}{c.company ? ` · ${c.company}` : ''}</div>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-neutral-500 max-w-[200px] truncate" title={c.destination_url}>{c.destination_url}</td>
                  <td className="px-4 py-3 text-neutral-700">{c.source}</td>
                  <td className="px-4 py-3 text-neutral-700">{c.medium}</td>
                  <td className="px-4 py-3">
                    <span className="uppercase tracking-[0.18em] text-[10px] text-[#C8A15A]">{c.status}</span>
                  </td>
                  <td className="px-4 py-3 text-[11px] text-neutral-500">{c.created_at ? new Date(c.created_at).toLocaleDateString() : ''}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1 justify-end">
                      <button onClick={() => copyText(c.generated_url)} className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">Copy</button>
                      <a href={c.generated_url} target="_blank" rel="noreferrer" className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">Open</a>
                      <button onClick={() => setQrTarget({ url: c.generated_url, name: c.name })} className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">QR</button>
                      <button onClick={() => duplicate(c.id)} className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">Duplicate</button>
                      {c.status !== 'archived' ? (
                        <button onClick={() => patchStatus(c.id, 'archived')} className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">Archive</button>
                      ) : (
                        <button onClick={() => patchStatus(c.id, 'active')} className="text-[10px] px-2 py-1 border border-neutral-300 rounded hover:bg-neutral-100">Restore</button>
                      )}
                      <button onClick={() => remove(c.id)} className="text-[10px] px-2 py-1 border border-red-200 text-red-700 rounded hover:bg-red-50">Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="mt-6 text-[11px] text-neutral-400 max-w-2xl leading-relaxed">
        Per-campaign performance (sessions, users, conversions, bounce rate) will appear once Google Analytics begins collecting traffic and is connected to the Data API. Until then, you can view campaign performance directly inside Google Analytics using the <em>utm_campaign</em> values you set here.
      </p>

      {qrTarget && (
        <QRModal target={qrTarget} onClose={() => setQrTarget(null)} />
      )}
    </div>
  );
}

function TextField({ label, value, onChange, placeholder }) {
  return (
    <div>
      <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">{label}</label>
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900" />
    </div>
  );
}
function SelectField({ label, value, onChange, options }) {
  return (
    <div>
      <label className="uppercase tracking-[0.22em] text-[10px] text-neutral-500 mb-2 block">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full bg-white border border-neutral-200 rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-neutral-900">
        {options.map((o) => <option key={o} value={o}>{o || '— none —'}</option>)}
      </select>
    </div>
  );
}

function QRModal({ target, onClose }) {
  const png = qrUrl(target.url, 'png');
  const svg = qrUrl(target.url, 'svg');
  const clean = (target.name || 'campaign').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'campaign';
  return (
    <div className="fixed inset-0 z-[70] bg-neutral-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl max-w-[500px] w-full shadow-2xl overflow-hidden">
        <div className="px-6 py-4 border-b border-neutral-200/60 flex justify-between items-center">
          <div className="uppercase tracking-[0.22em] text-[10px] text-neutral-500">QR Code</div>
          <button onClick={onClose} className="text-neutral-500 hover:text-neutral-900 text-xl px-2" aria-label="Close">×</button>
        </div>
        <div className="px-6 py-8 text-center">
          <img src={png} alt={`QR code for ${target.name}`} className="mx-auto w-64 h-64 border border-neutral-200 rounded" />
          <div className="mt-6 text-[13px] text-neutral-900" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>{target.name}</div>
          <div className="mt-1 text-[11px] text-neutral-500 break-all">{target.url}</div>
          <div className="mt-6 flex justify-center gap-2">
            <a href={png} download={`${clean}-qr.png`} className="text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100">Download PNG</a>
            <a href={svg} download={`${clean}-qr.svg`} className="text-[10px] uppercase tracking-[0.2em] px-4 py-2.5 rounded-full border border-neutral-300 hover:bg-neutral-100">Download SVG</a>
          </div>
        </div>
      </div>
    </div>
  );
}
